import { useEffect, useState, useRef, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { LogOut, Bell } from "lucide-react";
import { toast } from "sonner";

const NEXT = { NEW: "ACKNOWLEDGED", ACKNOWLEDGED: "PREPARING", PREPARING: "READY", READY: "DELIVERED" };
const NEXT_LABEL = { NEW: "Acknowledge", ACKNOWLEDGED: "Start Preparing", PREPARING: "Mark Ready", READY: "Mark Delivered" };
const STATUS_COLOR = { NEW: "#C85A32", ACKNOWLEDGED: "#D9822B", PREPARING: "#B8860B", READY: "#2A4436" };

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; g.gain.value = 0.15;
    o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 260);
  } catch (e) {
    console.warn("Audio beep unavailable:", e);
  }
}

export default function KitchenDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ active: [], completed: [], alert_timeout_seconds: 60 });
  const [now, setNow] = useState(Date.now());
  const [audioEnabled, setAudioEnabled] = useState(false);
  const seen = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const r = await http.get("/kitchen/orders");
      const newIds = new Set(r.data.active.filter((o) => o.status === "NEW").map((o) => o.id));
      const fresh = [...newIds].filter((id) => !seen.current.has(id));
      seen.current = newIds;
      if (fresh.length > 0 && audioEnabled) playBeep();
      setData(r.data);
    } catch (e) {
      console.error("Kitchen orders refresh failed:", e);
    }
  }, [audioEnabled]);

  useEffect(() => { load(); const t = setInterval(load, 3000); const nt = setInterval(() => setNow(Date.now()), 1000); return () => { clearInterval(t); clearInterval(nt); }; }, [load]);

  const advance = async (o) => {
    try {
      await http.post(`/kitchen/orders/${o.id}/status`, { status: NEXT[o.status] });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  const cardBorder = (o) => {
    const age = (now - new Date(o.created_at).getTime()) / 1000;
    if (o.status === "NEW" && age > data.alert_timeout_seconds) return "2px solid #C85A32";
    return "1px solid #363E38";
  };

  return (
    <div className="kitchen-bg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="inline-block rounded-xl px-3 py-1.5 mb-2" style={{ background: "#F5F2EC" }}>
            <BrandLogo className="h-7" />
          </span>
          <div className="font-serif text-4xl" style={{ color: "#EAA14D" }}>Kitchen</div>
          <div className="text-sm opacity-70">{user?.name} · {new Date().toLocaleTimeString()}</div>
        </div>
        <div className="flex gap-3">
          {!audioEnabled && <button data-testid="kitchen-enable-audio" onClick={() => { setAudioEnabled(true); playBeep(); }} className="kitchen-btn" style={{ background: "#D9822B", color: "#fff" }}><Bell size={16} className="inline mr-2" />Enable Sound</button>}
          <button data-testid="kitchen-logout" onClick={doLogout} className="kitchen-btn" style={{ background: "#363E38", color: "#F5F2EC" }}><LogOut size={16} className="inline mr-2" />Sign Out</button>
        </div>
      </div>

      <div className="mb-3 text-xs uppercase tracking-widest opacity-70">Active Orders ({data.active.length})</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {data.active.map((o) => {
          const age = Math.floor((now - new Date(o.created_at).getTime()) / 1000);
          const overdue = o.status === "NEW" && age > data.alert_timeout_seconds;
          return (
            <div key={o.id} className={`kitchen-card ${overdue ? "pulse-alert" : ""}`} style={{ border: cardBorder(o) }} data-testid={`kitchen-order-${o.order_number}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-70">{o.order_number}</div>
                  <div className="font-serif text-2xl">{o.guest_name || "Guest"}</div>
                </div>
                <div className="text-xs px-3 py-1 rounded-full font-bold" style={{ background: STATUS_COLOR[o.status] || "#3E5D4B", color: "#fff" }}>{o.status}</div>
              </div>
              <div className="text-3xl font-mono mb-3" style={{ color: overdue ? "#E06D44" : "#EAA14D" }}>
                {Math.floor(age / 60)}:{String(age % 60).padStart(2, "0")}
              </div>
              <div className="space-y-1 mb-4">
                {o.items.map((it) => <div key={it.id} className="text-base"><b>{it.quantity}×</b> {it.item_name_snapshot}</div>)}
              </div>
              {o.special_instruction && <div className="text-sm mb-4 p-2 rounded" style={{ background: "#141715", color: "#EAA14D" }}>Note: {o.special_instruction}</div>}
              {o.status !== "DELIVERED" && (
                <button data-testid={`kitchen-advance-${o.order_number}`} onClick={() => advance(o)} className="kitchen-btn w-full" style={{ background: "#C85A32", color: "#fff" }}>
                  {NEXT_LABEL[o.status]}
                </button>
              )}
            </div>
          );
        })}
        {data.active.length === 0 && <div className="opacity-60 col-span-full text-center py-12 font-serif text-2xl">All caught up 🍃</div>}
      </div>

      <div className="mb-3 text-xs uppercase tracking-widest opacity-70">Recently Completed</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.completed.map((o) => (
          <div key={o.id} className="kitchen-card" style={{ opacity: 0.7 }}>
            <div className="text-xs opacity-70">{o.order_number}</div>
            <div className="font-serif text-lg">{o.guest_name}</div>
            <div className="text-xs opacity-70">{o.items.length} item(s) · Delivered</div>
          </div>
        ))}
      </div>
    </div>
  );
}
